import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { products } from '../data/products';
import { getPersonalizationSchema } from '../data/productPersonalizationSchemas';
import PremiumPaywall from '../components/PremiumPaywall';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, Loader2, CheckCircle2, Sparkles,
  Home, RefreshCw, Lock, Eye, ShoppingBag, User, Briefcase
} from 'lucide-react';
import VisualProductView from '../components/VisualProductView';
import DocumentProductView from '../components/DocumentProductView';
import UserNavMenu from '../components/UserNavMenu';
import LumeraLogo from '../components/LumeraLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Visual Select Card ───
const VisualSelectCard = ({ option, selected, onClick }) => (
  <button
    data-testid={`option-${option.id}`}
    onClick={onClick}
    className={`relative group text-left rounded-2xl border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 overflow-hidden ${
      selected ? 'border-[#C49A6C] ring-2 ring-[#C49A6C]/20 shadow-md' : 'border-stone-100 bg-white hover:border-[#C49A6C]/30'
    }`}
  >
    {/* Gradient preview bar */}
    <div className={`h-16 w-full transition-all duration-300 ${
      option.id.includes('minimal') ? 'bg-gradient-to-br from-stone-50 to-stone-100' :
      option.id.includes('romantic') || option.id.includes('feminine') || option.id.includes('blush') ? 'bg-gradient-to-br from-pink-50 to-rose-100' :
      option.id.includes('botanical') || option.id.includes('sage') ? 'bg-gradient-to-br from-emerald-50 to-green-100' :
      option.id.includes('luxury') || option.id.includes('editorial') || option.id.includes('bold') ? 'bg-gradient-to-br from-stone-800 to-stone-900' :
      option.id.includes('coastal') || option.id.includes('dusty-blue') ? 'bg-gradient-to-br from-sky-50 to-blue-100' :
      option.id.includes('rustic') || option.id.includes('boho') || option.id.includes('earthy') || option.id.includes('warm') ? 'bg-gradient-to-br from-amber-50 to-orange-100' :
      option.id.includes('modern') || option.id.includes('sport') || option.id.includes('energetic') ? 'bg-gradient-to-br from-indigo-50 to-violet-100' :
      option.id.includes('pastel') || option.id.includes('soft') ? 'bg-gradient-to-br from-purple-50 to-pink-50' :
      option.id.includes('playful') || option.id.includes('colorful') || option.id.includes('cute') ? 'bg-gradient-to-br from-yellow-50 to-orange-50' :
      option.id.includes('vintage') ? 'bg-gradient-to-br from-amber-50 to-yellow-100' :
      option.id.includes('classic') || option.id.includes('cozy') ? 'bg-gradient-to-br from-orange-50 to-amber-50' :
      option.id.includes('neutral') ? 'bg-gradient-to-br from-stone-50 to-stone-100' :
      option.id.includes('professional') ? 'bg-gradient-to-br from-slate-50 to-slate-100' :
      'bg-gradient-to-br from-stone-50 to-amber-50'
    }`}>
      {selected && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#C49A6C] flex items-center justify-center shadow-sm">
          <CheckCircle2 className="w-3.5 h-3.5 text-white" />
        </div>
      )}
    </div>
    <div className="p-4">
      <h3 className="font-sans font-semibold text-stone-800 text-sm mb-0.5">{option.label}</h3>
      {option.desc && <p className="text-xs font-sans text-stone-500 leading-relaxed">{option.desc}</p>}
    </div>
  </button>
);

// ─── Color Palette Swatch ───
const ColorPaletteSwatch = ({ option, selected, onClick }) => (
  <button
    data-testid={`palette-${option.id}`}
    onClick={onClick}
    className={`relative text-left rounded-2xl border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 overflow-hidden ${
      selected ? 'border-[#C49A6C] ring-2 ring-[#C49A6C]/20 shadow-md' : 'border-stone-100 bg-white hover:border-[#C49A6C]/30'
    }`}
  >
    {/* Color strip preview */}
    <div className="flex h-14">
      {option.colors?.length > 0 ? option.colors.map((c, i) => (
        <div key={i} className="flex-1 transition-all" style={{ backgroundColor: c }} />
      )) : (
        <div className="flex-1 bg-gradient-to-r from-stone-100 via-stone-200 to-stone-100 flex items-center justify-center">
          <span className="text-xs text-stone-400 font-sans">Custom</span>
        </div>
      )}
    </div>
    <div className="p-3 flex items-center justify-between">
      <p className="text-xs font-sans font-semibold text-stone-700">{option.label}</p>
      {selected && <CheckCircle2 className="w-4 h-4 text-[#C49A6C]" />}
    </div>
  </button>
);

// ─── Multi Select Chip ───
const MultiSelectChip = ({ option, selected, onClick }) => (
  <button
    data-testid={`chip-${option.id}`}
    onClick={onClick}
    className={`px-4 py-2.5 rounded-full border-2 text-sm font-sans font-medium transition-all ${
      selected
        ? 'border-[#C49A6C] bg-[#C49A6C]/10 text-[#8B6F47]'
        : 'border-stone-200 bg-white text-stone-600 hover:border-[#C49A6C]/30'
    }`}
  >
    {selected && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
    {option.label}
  </button>
);

// ═══════════════════════════════════════
// PRODUCT PERSONALIZER PAGE
// ═══════════════════════════════════════
const ProductPersonalizer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isPro, loading: authLoading, hasProductAccess, fetchPurchases, purchasesLoaded } = useAuth();

  const productSlug = searchParams.get('product');
  const product = products.find(p => p.slug === productSlug);
  const schema = productSlug ? getPersonalizationSchema(productSlug) : null;

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [useMode, setUseMode] = useState(null); // 'personal' | 'creator'
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadedProjectId, setLoadedProjectId] = useState(null);

  // License type recommendation based on wizard answer
  const recommendedLicenseType = useMode === 'creator' ? 'commercial' : 'personal';

  // Preview/paywall
  const [previewAccess, setPreviewAccess] = useState(null);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);

  const totalSteps = schema ? schema.steps.length : 0;
  const showCreatorStep = schema?.showCreatorToggle && useMode === null;

  // ── Auth redirect ──
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (!productSlug || !schema) { navigate('/generator'); return; }
  }, [user, authLoading, productSlug, schema, navigate]);

  // ── Check product access ──
  useEffect(() => {
    if (authLoading || !user || !productSlug || !purchasesLoaded) return;
    const checkAccess = async () => {
      try {
        const res = await axios.get(`${API}/purchases/preview-access/${productSlug}`);
        setPreviewAccess(res.data.access);
        setPreviewProduct(res.data.product);
      } catch {
        setPreviewAccess('full');
      }
    };
    checkAccess();
  }, [productSlug, authLoading, user, purchasesLoaded]);

  // ── Setters ──
  const setAnswer = useCallback((fieldId, value) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
  }, []);

  const toggleMultiSelect = useCallback((fieldId, optionId) => {
    setAnswers(prev => {
      const current = prev[fieldId] || [];
      const next = current.includes(optionId)
        ? current.filter(id => id !== optionId)
        : [...current, optionId];
      return { ...prev, [fieldId]: next };
    });
  }, []);

  // ── Step validation ──
  const isStepValid = useCallback(() => {
    if (!schema) return false;
    const currentStep = schema.steps[step];
    if (!currentStep) return false;

    if (currentStep.type === 'visual-select' || currentStep.type === 'color-palette') {
      return !currentStep.required || !!answers[currentStep.field];
    }
    if (currentStep.type === 'multi-select') {
      return !currentStep.required || (answers[currentStep.field]?.length > 0);
    }
    if (currentStep.fields) {
      return currentStep.fields.every(f => {
        if (!f.required) return true;
        if (f.type === 'multi-select-inline') return (answers[f.id]?.length > 0);
        return !!answers[f.id];
      });
    }
    return true;
  }, [schema, step, answers]);

  // ── Generation ──
  const handleGenerate = async () => {
    if (!schema || !product) return;
    setError('');
    setGenerating(true);

    try {
      // Build generation payload with personalization data
      const personalization = { ...answers, useMode: useMode || 'personal' };

      // Build a topic from personalization answers
      let topic = product.title;
      if (answers.partner1 && answers.partner2) topic = `${answers.partner1} & ${answers.partner2}'s Wedding Planner`;
      else if (answers.destination) topic = `${answers.destination} ${product.title}`;
      else if (answers.babyName) topic = `${answers.babyName}'s ${product.title}`;
      else if (answers.puppyName) topic = `${answers.puppyName}'s ${product.title}`;
      else if (answers.petName) topic = `${answers.petName}'s ${product.title}`;
      else if (answers.bookTitle) topic = answers.bookTitle;
      else if (answers.brandName) topic = `${answers.brandName} ${product.title}`;

      let audience = useMode === 'creator' ? (answers.targetBuyer || 'Digital product buyers') : 'Personal use';

      const payload = {
        productType: schema.productType,
        topic,
        audience,
        painPoint: '',
        tone: 'friendly',
        language: 'en',
        length: 'medium',
        outputSize: 'a4-document',
        product_slug: productSlug,
        personalization,
      };

      const startRes = await axios.post(`${API}/content/generate`, payload, { timeout: 15000 });
      const taskId = startRes.data.task_id;

      let attempts = 0;
      const maxAttempts = 60;
      let consecutive404s = 0;
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const pollRes = await axios.get(`${API}/content/tasks/${taskId}?_t=${Date.now()}`, { timeout: 10000 });
          consecutive404s = 0;
          if (pollRes.data.status === 'complete') {
            setGeneratedContent(pollRes.data.result);
            setGenerating(false);
            return;
          } else if (pollRes.data.status === 'failed') {
            setError(pollRes.data.error || 'Generation failed. Please try again.');
            setGenerating(false);
            return;
          }
        } catch (pollErr) {
          if (pollErr.response?.status === 404) {
            consecutive404s++;
            if (consecutive404s >= 5) throw pollErr;
            continue;
          }
          if (attempts >= maxAttempts) throw pollErr;
        }
      }
      setError('Generation took too long. Please try again.');
    } catch (err) {
      if (err.response?.status === 403) {
        setShowPaywall(true);
        setGenerating(false);
        return;
      }
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.message || 'Generation failed. Please try again.');
    }
    setGenerating(false);
  };

  const handleSaveProject = async () => {
    if (!generatedContent) return;
    setSaving(true);
    try {
      await axios.post(`${API}/projects/`, {
        product_type: schema.productType,
        title: generatedContent.title || product.title,
        content: generatedContent,
        design_settings: { tone: 'friendly', style: 'modern', personalization: answers }
      });
      navigate('/dashboard');
    } catch {
      setError('Could not save project.');
    }
    setSaving(false);
  };

  const handleUpdateContent = (newContent) => setGeneratedContent(newContent);
  const handleEditSettings = () => { setGeneratedContent(null); setStep(0); };

  if (!user || authLoading || !schema || !product) return null;

  // ── Result view ──
  if (generatedContent) {
    return (
      <div data-testid="personalizer-page" className="min-h-screen bg-[#F7F5F2] dark:bg-background">
        <nav className="bg-white dark:bg-card border-b border-[#d8cfc8]/50 dark:border-border/50 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
            <LumeraLogo size="default" />
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-stone-600 font-sans text-sm">
                <Home className="w-4 h-4 mr-1.5" /> Dashboard
              </Button>
              <UserNavMenu size={34} />
            </div>
          </div>
        </nav>
        <div className="max-w-7xl mx-auto px-6 py-10">
          {generatedContent.slides ? (
            <VisualProductView content={generatedContent} onSave={handleSaveProject}
              onNew={() => { setGeneratedContent(null); setStep(0); setAnswers({}); }}
              saving={saving} onUpdateContent={handleUpdateContent} onEditSettings={handleEditSettings}
              projectId={loadedProjectId} outputSize="a4-document" productSlug={productSlug} />
          ) : (
            <DocumentProductView content={generatedContent} productType={schema.productType}
              onSave={handleSaveProject}
              onNew={() => { setGeneratedContent(null); setStep(0); setAnswers({}); }}
              saving={saving} onUpdateContent={handleUpdateContent} onEditSettings={handleEditSettings}
              projectId={loadedProjectId} outputSize="a4-document" productSlug={productSlug} />
          )}
        </div>
      </div>
    );
  }

  // ── Paywall ──
  if (showPaywall) {
    return (
      <div data-testid="personalizer-page" className="min-h-screen bg-[#F7F5F2] dark:bg-background">
        <nav className="bg-white dark:bg-card border-b border-[#d8cfc8]/50 dark:border-border/50 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
            <LumeraLogo size="default" />
            <UserNavMenu size={34} />
          </div>
        </nav>
        <div className="max-w-4xl mx-auto px-6 py-10">
          <PremiumPaywall product={previewProduct} productSlug={productSlug} previewStep={step + 1} totalPreviewSteps={totalSteps} />
        </div>
      </div>
    );
  }

  const currentStep = schema.steps[step];

  // ── Render a step's field ──
  const renderField = (field) => {
    if (field.type === 'text') {
      return (
        <div key={field.id}>
          <label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">
            {field.label} {field.required && <span className="text-red-400">*</span>}
          </label>
          <Input data-testid={`field-${field.id}`} value={answers[field.id] || ''} onChange={e => setAnswer(field.id, e.target.value)}
            placeholder={field.placeholder || ''} className="bg-stone-50 border-stone-200 focus:border-[#C49A6C] focus:ring-2 focus:ring-[#C49A6C]/20 rounded-xl px-4 py-3" />
        </div>
      );
    }
    if (field.type === 'textarea') {
      return (
        <div key={field.id}>
          <label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">
            {field.label} {field.required && <span className="text-red-400">*</span>}
          </label>
          <Textarea data-testid={`field-${field.id}`} value={answers[field.id] || ''} onChange={e => setAnswer(field.id, e.target.value)}
            placeholder={field.placeholder || ''} rows={3} className="bg-stone-50 border-stone-200 focus:border-[#C49A6C] focus:ring-2 focus:ring-[#C49A6C]/20 rounded-xl px-4 py-3" />
        </div>
      );
    }
    if (field.type === 'date') {
      return (
        <div key={field.id}>
          <label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">
            {field.label} {field.required && <span className="text-red-400">*</span>}
          </label>
          <Input data-testid={`field-${field.id}`} type="date" value={answers[field.id] || ''} onChange={e => setAnswer(field.id, e.target.value)}
            className="bg-stone-50 border-stone-200 focus:border-[#C49A6C] focus:ring-2 focus:ring-[#C49A6C]/20 rounded-xl px-4 py-3" />
        </div>
      );
    }
    if (field.type === 'number') {
      return (
        <div key={field.id}>
          <label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">
            {field.label} {field.required && <span className="text-red-400">*</span>}
          </label>
          <Input data-testid={`field-${field.id}`} type="number" value={answers[field.id] || ''} onChange={e => setAnswer(field.id, e.target.value)}
            placeholder={field.placeholder || ''} min={field.min} max={field.max}
            className="bg-stone-50 border-stone-200 focus:border-[#C49A6C] focus:ring-2 focus:ring-[#C49A6C]/20 rounded-xl px-4 py-3 w-32" />
        </div>
      );
    }
    if (field.type === 'select') {
      return (
        <div key={field.id}>
          <label className="text-sm font-sans font-medium text-stone-700 mb-2 block">
            {field.label} {field.required && <span className="text-red-400">*</span>}
          </label>
          <div className="flex flex-wrap gap-2">
            {field.options.map(opt => (
              <button key={opt.id} data-testid={`select-${field.id}-${opt.id}`}
                onClick={() => setAnswer(field.id, opt.id)}
                className={`px-4 py-2.5 rounded-xl border-2 text-sm font-sans font-medium transition-all ${
                  answers[field.id] === opt.id
                    ? 'border-[#C49A6C] bg-[#C49A6C]/10 text-[#8B6F47]'
                    : 'border-stone-200 text-stone-600 hover:border-[#C49A6C]/30'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (field.type === 'multi-select-inline') {
      return (
        <div key={field.id}>
          <label className="text-sm font-sans font-medium text-stone-700 mb-2 block">
            {field.label} {field.required && <span className="text-red-400">*</span>}
          </label>
          <div className="flex flex-wrap gap-2">
            {field.options.map(opt => (
              <MultiSelectChip key={opt.id} option={opt} selected={(answers[field.id] || []).includes(opt.id)}
                onClick={() => toggleMultiSelect(field.id, opt.id)} />
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div data-testid="personalizer-page" className="min-h-screen bg-[#F7F5F2] dark:bg-background">
      {/* Nav */}
      <nav className="bg-white dark:bg-card border-b border-[#d8cfc8]/50 dark:border-border/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <LumeraLogo size="default" />
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-stone-600 font-sans text-sm">
              <Home className="w-4 h-4 mr-1.5" /> Dashboard
            </Button>
            <UserNavMenu size={34} />
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Product header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C49A6C]/10 text-[#8B6F47] text-xs font-sans font-semibold mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Personalizing: {product.title}
          </div>
        </div>

        {/* Preview indicator */}
        {previewAccess === 'preview' && (
          <div className="flex items-center justify-center gap-2 mb-4">
            <Eye className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-sans font-medium text-amber-700 bg-amber-50 px-3 py-1 rounded-full">
              Free preview · Step {step + 1} of {totalSteps}
            </span>
          </div>
        )}

        {/* Creator/Personal toggle */}
        {showCreatorStep && (
          <div data-testid="use-mode-select" className="animate-fade-in-up mb-8">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-2 text-center">I'm creating this for...</h2>
            <p className="text-stone-500 font-sans mb-8 text-center">This helps us ask the right questions</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-2xl mx-auto">
              {[
                { id: 'personal', icon: User, label: 'Myself / personal use', desc: 'Personalize it for you' },
                { id: 'business', icon: Briefcase, label: 'My business / clients', desc: 'For your brand or customers' },
                { id: 'creator', icon: ShoppingBag, label: 'A product I plan to sell', desc: 'For your digital product shop' },
              ].map(mode => (
                <button key={mode.id} data-testid={`mode-${mode.id}`}
                  onClick={() => {
                    const isCreatorOrBusiness = mode.id === 'creator' || mode.id === 'business';
                    setUseMode(isCreatorOrBusiness ? 'creator' : 'personal');
                    // Store license recommendation for purchase flow
                    sessionStorage.setItem('lumera_license_recommendation', isCreatorOrBusiness ? 'commercial' : 'personal');
                  }}
                  className="text-left p-5 rounded-2xl border-2 border-stone-200 bg-white hover:border-[#C49A6C]/40 hover:shadow-md transition-all"
                >
                  <mode.icon className="w-5 h-5 text-[#C49A6C] mb-2" />
                  <h3 className="font-sans font-semibold text-stone-800 text-sm">{mode.label}</h3>
                  <p className="text-xs font-sans text-stone-500 mt-0.5">{mode.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Progress bar */}
        {(useMode !== null || !schema?.showCreatorToggle) && !generating && (
          <>
            <div className="flex items-center justify-center gap-1.5 mb-10">
              {schema.steps.map((s, i) => (
                <React.Fragment key={s.id}>
                  <button
                    onClick={() => { if (i < step) setStep(i); }}
                    className={`flex items-center gap-1.5 ${i < step ? 'cursor-pointer' : ''}`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-sans font-semibold ${
                      i < step ? 'bg-[#C49A6C] text-white' : i === step ? 'bg-stone-800 text-white' : 'bg-stone-200 text-stone-400'
                    }`}>
                      {i < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                    </div>
                    <span className="hidden md:inline text-xs font-sans font-medium text-stone-500">{s.title.split(' ').slice(0, 3).join(' ')}</span>
                  </button>
                  {i < totalSteps - 1 && <div className={`w-5 md:w-10 h-0.5 ${i < step ? 'bg-[#C49A6C]' : 'bg-stone-200'}`} />}
                </React.Fragment>
              ))}
            </div>

            {error && (
              <div data-testid="personalizer-error" className="bg-red-50 border border-red-200 text-red-700 text-sm font-sans rounded-xl px-4 py-3 mb-6">
                {error}
              </div>
            )}

            {/* ── Current Step Content ── */}
            <div className="animate-fade-in-up">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-2">{currentStep.title}</h2>
              <p className="text-stone-500 font-sans mb-8">{currentStep.subtitle}</p>

              {/* Creator fields (shown first on step 0 for creators) */}
              {useMode === 'creator' && step === 0 && schema.creatorFields && (
                <div className="bg-amber-50/50 border border-amber-100 rounded-2xl p-5 mb-6 space-y-4">
                  <p className="text-xs font-sans font-semibold text-amber-700 uppercase tracking-wider">Creator details</p>
                  {schema.creatorFields.map(f => renderField(f))}
                </div>
              )}

              {/* Fields-based step */}
              {currentStep.fields && (
                <div className="bg-white rounded-2xl border border-stone-100 p-6 md:p-8 space-y-5">
                  {currentStep.fields.map(f => renderField(f))}
                </div>
              )}

              {/* Visual select step */}
              {currentStep.type === 'visual-select' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">
                  {currentStep.options.map(opt => (
                    <VisualSelectCard key={opt.id} option={opt} selected={answers[currentStep.field] === opt.id}
                      onClick={() => setAnswer(currentStep.field, opt.id)} />
                  ))}
                </div>
              )}

              {/* Color palette step */}
              {currentStep.type === 'color-palette' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">
                  {currentStep.options.map(opt => (
                    <ColorPaletteSwatch key={opt.id} option={opt} selected={answers[currentStep.field] === opt.id}
                      onClick={() => setAnswer(currentStep.field, opt.id)} />
                  ))}
                </div>
              )}

              {/* Multi-select step */}
              {currentStep.type === 'multi-select' && (
                <div className="flex flex-wrap gap-2.5">
                  {currentStep.options.map(opt => (
                    <MultiSelectChip key={opt.id} option={opt}
                      selected={(answers[currentStep.field] || []).includes(opt.id)}
                      onClick={() => toggleMultiSelect(currentStep.field, opt.id)} />
                  ))}
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={() => { if (step > 0) setStep(step - 1); else if (schema.showCreatorToggle) setUseMode(null); else navigate(-1); }}
                  className="font-sans font-medium text-stone-600">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>

                {step < totalSteps - 1 ? (
                  <Button data-testid="next-step-btn" onClick={() => { if (isStepValid()) { setError(''); setStep(step + 1); } else setError('Please complete the required fields.'); }}
                    className="bg-stone-900 text-white hover:bg-stone-800 rounded-full px-6 font-sans font-semibold shadow-lg">
                    Next <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                ) : (
                  <Button data-testid="generate-btn" onClick={() => { if (isStepValid()) handleGenerate(); else setError('Please complete the required fields.'); }}
                    disabled={generating}
                    className="bg-[#C49A6C] text-white hover:bg-[#B08A5C] rounded-full px-8 font-sans font-semibold shadow-lg">
                    {generating ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Generate My {product.title}</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {/* Generating state */}
        {generating && (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-[#C49A6C]/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-[#C49A6C] animate-spin" />
            </div>
            <h3 className="font-serif text-xl font-bold text-stone-900 mb-2">Creating your {product.title}...</h3>
            <p className="text-sm font-sans text-stone-500">This usually takes 15–30 seconds</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPersonalizer;
